import React, { useState, useCallback } from 'react'
import { ChevronRight, ChevronDown, Folder, FolderOpen, Loader2 } from 'lucide-react'
import type { TaxonomyNode } from '../../types'

interface TaxonomyTreeProps {
  nodes: TaxonomyNode[]
  onNodeSelect: (node: TaxonomyNode) => void
  selectedNodeId?: string
  onExpand: (node: TaxonomyNode) => Promise<TaxonomyNode[]>
}

interface TreeNodeState {
  expanded: boolean
  children: TaxonomyNode[]
  loading: boolean
  loaded: boolean
}

const TreeNodeItem: React.FC<{
  node: TaxonomyNode
  depth: number
  selectedNodeId?: string
  onNodeSelect: (node: TaxonomyNode) => void
  onExpand: (node: TaxonomyNode) => Promise<TaxonomyNode[]>
}> = ({ node, depth, selectedNodeId, onNodeSelect, onExpand }) => {
  const [state, setState] = useState<TreeNodeState>({
    expanded: false,
    children: [],
    loading: false,
    loaded: false,
  })

  const isSelected = selectedNodeId === node.node_id
  const hasChildren = node.children_count > 0

  const handleToggle = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!hasChildren) return

    if (!state.loaded) {
      setState(prev => ({ ...prev, loading: true }))
      try {
        const children = await onExpand(node)
        setState({ expanded: true, children, loading: false, loaded: true })
      } catch {
        setState(prev => ({ ...prev, loading: false }))
      }
    } else {
      setState(prev => ({ ...prev, expanded: !prev.expanded }))
    }
  }, [hasChildren, state.loaded, node, onExpand])

  const handleSelect = useCallback(() => {
    onNodeSelect(node)
  }, [node, onNodeSelect])

  return (
    <div>
      <div
        className={`
          flex items-center gap-1.5 py-1.5 px-2 cursor-pointer rounded-md text-sm
          transition-colors group
          ${isSelected
            ? 'bg-acad-50 border-l-2 border-acad-500 text-acad-700 font-medium'
            : 'hover:bg-gray-50 border-l-2 border-transparent text-gray-700'
          }
        `}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={handleSelect}
      >
        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <button
            onClick={handleToggle}
            className="p-0.5 rounded hover:bg-gray-200 transition-colors shrink-0"
          >
            {state.loading ? (
              <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
            ) : state.expanded ? (
              <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
            )}
          </button>
        ) : (
          <span className="w-4.5 shrink-0" />
        )}

        {/* Folder icon */}
        {state.expanded ? (
          <FolderOpen className="w-4 h-4 text-acad-500 shrink-0" />
        ) : (
          <Folder className="w-4 h-4 text-gray-400 group-hover:text-acad-400 shrink-0" />
        )}

        {/* Label */}
        <span className="truncate flex-1">{node.label}</span>

        {/* Resource count badge */}
        {node.resource_count > 0 && (
          <span className={`
            text-[10px] px-1.5 py-0.5 rounded-full shrink-0
            ${isSelected
              ? 'bg-acad-100 text-acad-700'
              : 'bg-gray-100 text-gray-500'
            }
          `}>
            {node.resource_count.toLocaleString()}
          </span>
        )}
      </div>

      {/* Children */}
      {state.expanded && state.children.length > 0 && (
        <div>
          {state.children.map(child => (
            <TreeNodeItem
              key={child.node_id}
              node={child}
              depth={depth + 1}
              selectedNodeId={selectedNodeId}
              onNodeSelect={onNodeSelect}
              onExpand={onExpand}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const TaxonomyTree: React.FC<TaxonomyTreeProps> = ({
  nodes,
  onNodeSelect,
  selectedNodeId,
  onExpand,
}) => {
  if (nodes.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-gray-400">
        No taxonomy nodes available
      </div>
    )
  }

  return (
    <div className="py-1">
      {nodes.map(node => (
        <TreeNodeItem
          key={node.node_id}
          node={node}
          depth={0}
          selectedNodeId={selectedNodeId}
          onNodeSelect={onNodeSelect}
          onExpand={onExpand}
        />
      ))}
    </div>
  )
}

export default TaxonomyTree
